import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Card,
  CardContent,
  Button,
  Checkbox,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import PageHeader from '@/components/PageHeader';
import StatusChip from '@/components/StatusChip';
import UserCreateDialog from '@/components/UserCreateDialog';
import UserEditDialog from '@/components/UserEditDialog';
import { useAuth } from '@/auth/AuthProvider';
import { deleteUser, listUsers, type ApiUser } from '@/services/userApi';

const roleIcons = {
  Admin: 'Admin',
  Manager: 'Manager',
  User: 'User',
  Viewer: 'Viewer',
} as const;

export default function UsersPage() {
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'Admin';
  const isManager = currentUser?.role === 'Manager';
  const [users, setUsers] = useState<ApiUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<ApiUser | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [processing, setProcessing] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);

  const loadUsers = async () => {
    setLoading(true);
    setErrorMessage('');

    try {
      const response = await listUsers({ limit: 200, offset: 0 });
      setUsers(response.items);
    } catch {
      setErrorMessage('Nie udało się pobrać listy osób.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadUsers();
  }, []);

  const canManageUser = (targetUser: ApiUser) => {
    if (targetUser.id === currentUser?.id) return false;
    if (isAdmin) return true;
    return isManager && ['User', 'Viewer'].includes(targetUser.role);
  };

  const deletableUsers = users.filter(canManageUser);
  const selectedDeletableUsers = deletableUsers.filter((item) => selectedUserIds.includes(item.id));
  const allDeletableSelected = deletableUsers.length > 0 && selectedDeletableUsers.length === deletableUsers.length;

  const toggleUserSelection = (userId: string) => {
    setSelectedUserIds((current) => current.includes(userId)
      ? current.filter((id) => id !== userId)
      : [...current, userId]);
  };

  const toggleAllDeletableUsers = () => {
    if (allDeletableSelected) {
      setSelectedUserIds((current) => current.filter((id) => !deletableUsers.some((item) => item.id === id)));
      return;
    }
    setSelectedUserIds((current) => Array.from(new Set([
      ...current.filter((id) => !users.some((item) => item.id === id)),
      ...deletableUsers.map((item) => item.id),
    ])));
  };

  const handleDeleteSelectedUsers = async () => {
    if (processing || selectedDeletableUsers.length === 0) return;

    const names = selectedDeletableUsers.map((item) => item.name).join(', ');
    const confirmed = window.confirm(
      `Czy na pewno usunąć zaznaczone konta (${selectedDeletableUsers.length})?\n\n${names}\n\nTej operacji nie można cofnąć.`,
    );
    if (!confirmed) return;

    setProcessing(true);
    setErrorMessage('');

    try {
      await Promise.all(selectedDeletableUsers.map((item) => deleteUser(item.id)));
      setSelectedUserIds([]);
      await loadUsers();
    } catch {
      setErrorMessage('Nie udało się usunąć wszystkich zaznaczonych kont. Odśwież listę i sprawdź wynik.');
      await loadUsers();
    } finally {
      setProcessing(false);
    }
  };

  const handleEditUser = (user: ApiUser) => {
    setSelectedUser(user);
    setEditDialogOpen(true);
  };

  const roleLabels = {
    Admin: 'Admin',
    Manager: 'Manager',
    User: 'User',
    Viewer: 'Viewer',
  } as const;

  const teamLabels = useMemo(() => ({
    Operations: 'Operacje',
    Warehouse: 'Magazyn',
    Service: 'Serwis',
    Audit: 'Audyt',
  }), []);

  const activeAdmins = users.filter((user) => user.role === 'Admin' && user.active).length;
  const viewerAccounts = users.filter((user) => user.role === 'Viewer').length;

  return (
    <Stack spacing={3}>
      <PageHeader
        eyebrow="People"
        title="Użytkownicy i dostęp"
        description="Lista ludzi w systemie, z możliwością dodawania nowych kont bezpośrednio z panelu."
        action={
          <Stack direction="row" spacing={1.5}>
            {isAdmin || isManager ? (
              <Button
                variant="outlined"
                color="error"
                onClick={() => void handleDeleteSelectedUsers()}
                disabled={processing || loading || selectedDeletableUsers.length === 0}
              >
                Usuń zaznaczone ({selectedDeletableUsers.length})
              </Button>
            ) : null}
            {isAdmin ? (
              <Button variant="contained" onClick={() => setCreateDialogOpen(true)} disabled={processing}>
                Dodaj osobę
              </Button>
            ) : null}
          </Stack>
        }
      />

      {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Stack spacing={1.5}>
                <Typography variant="overline" color="text.secondary">Łącznie użytkowników</Typography>
                <Typography variant="h4">{users.length}</Typography>
                <Typography variant="body2" color="text.secondary">Aktywny roster pobierany z backendu.</Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Stack spacing={1.5}>
                <Typography variant="overline" color="text.secondary">Aktywni administratorzy</Typography>
                <Typography variant="h4">{activeAdmins}</Typography>
                <Typography variant="body2" color="text.secondary">Konta z rolą Admin i aktywnym dostępem.</Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Stack spacing={1.5}>
                <Typography variant="overline" color="text.secondary">Viewer accounts</Typography>
                <Typography variant="h4">{viewerAccounts}</Typography>
                <Typography variant="body2" color="text.secondary">Konta tylko do podglądu i audytu.</Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card>
        <CardContent sx={{ p: 0 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={allDeletableSelected}
                      indeterminate={selectedDeletableUsers.length > 0 && !allDeletableSelected}
                      onChange={toggleAllDeletableUsers}
                      disabled={loading || deletableUsers.length === 0 || processing}
                      inputProps={{ 'aria-label': 'Zaznacz wszystkie konta możliwe do usunięcia' }}
                    />
                  </TableCell>
                  <TableCell>Osoba</TableCell>
                  <TableCell>E-mail</TableCell>
                  <TableCell>Rola</TableCell>
                  <TableCell>Zespół</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Ostatnie logowanie</TableCell>
                  <TableCell>Akcje</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => {
                  const roleKey = user.role as keyof typeof roleIcons;
                  const roleLabel = roleIcons[roleKey];
                  const translatedRole = roleLabels[roleKey];

                  return (
                    <TableRow key={user.id} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedUserIds.includes(user.id)}
                          onChange={() => toggleUserSelection(user.id)}
                          disabled={!canManageUser(user) || processing}
                          inputProps={{ 'aria-label': `Zaznacz użytkownika ${user.name}` }}
                        />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Avatar sx={{ width: 36, height: 36 }}>{user.name.slice(0, 2)}</Avatar>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{user.name}</Typography>
                            <Typography variant="caption" color="text.secondary">{user.id}</Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Box sx={{ width: 28, height: 28, borderRadius: 999, display: 'grid', placeItems: 'center', bgcolor: 'rgba(148,163,184,0.16)', color: 'text.secondary', fontWeight: 800 }}>{roleLabel.slice(0, 2)}</Box>
                          <StatusChip label={translatedRole} tone={roleKey === 'Admin' ? 'error' : roleKey === 'Manager' ? 'warning' : roleKey === 'User' ? 'info' : 'neutral'} />
                        </Stack>
                      </TableCell>
                      <TableCell>{user.team ? teamLabels[user.team as keyof typeof teamLabels] ?? user.team : '—'}</TableCell>
                      <TableCell>
                        <StatusChip label={user.active ? 'Aktywny' : 'Nieaktywny'} tone={user.active ? 'success' : 'neutral'} />
                      </TableCell>
                      <TableCell>{user.last_login_at ? new Date(user.last_login_at).toLocaleString() : 'Brak logowania'}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <Button
                            variant="text"
                            size="small"
                            disabled={processing || !canManageUser}
                            onClick={() => handleEditUser(user)}
                          >
                            Edytuj
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8}>Ładowanie użytkowników...</TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <UserCreateDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onCreated={() => {
          void loadUsers();
        }}
      />

      <UserEditDialog
        open={editDialogOpen}
        user={selectedUser}
        canManageRole={isAdmin}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedUser(null);
        }}
        onUpdated={() => {
          void loadUsers();
        }}
      />
    </Stack>
  );
}